import { camelize } from '../../libs/camelize';
import { isRecord } from '../../libs/is';

type Collection<Item> = {
  total: number;
  page: number;
  perPage: number;
  items: Item[];
  paging: {
    next: string | null;
    previous: string | null;
    first: string;
    last: string;
  };
};

export type ApiResponse<Data> = Data extends (infer Item)[] ? Collection<Item> : Data;

export const noViewerError = new Error('There is no viewer');

// Responses need to be camelized, and if they are a collection response
// we want to reformat the data slightly to avoid awkward naming.
export function standardizeResponseObject<T = unknown>(data: T): ApiResponse<T> {
  if (isRecord(data) && data.data && Array.isArray(data.data)) {
    const items = data.data;
    delete data.data;
    Object.assign(data, { items });
  }

  return camelize<ApiResponse<T>>(data);
}

// Utilities for building the Accept header string.
// The header can either be represented as an object with optional
// resource and version keys, or an array of that object when requesting
// a response that has multiple resource versions.

export type MaybeArray<T> = T | T[];

export type ResourceDeclaration = {
  resource?: string;
  version?: string;
};

const defaultResource: ResourceDeclaration = { resource: '*', version: '3.4.1' };

export function buildAcceptHeader(accept: MaybeArray<ResourceDeclaration> = defaultResource) {
  if (Array.isArray(accept)) {
    return accept
      .map(({ resource = defaultResource.resource, version = defaultResource.version }) => {
        return `application/vnd.vimeo.${resource};version=${version}`;
      })
      .join(',');
  }

  const { resource = defaultResource.resource, version = defaultResource.version } = accept;
  return `application/vnd.vimeo.${resource};version=${version}`;
}

// Convert an unknown item to a string;
export function encodeQuery(target: unknown): string {
  if (typeof target === 'string') {
    return target;
  }

  if (typeof target === 'number' || typeof target === 'boolean') {
    return target.toString();
  }

  if (Array.isArray(target)) {
    return target.map(encodeQuery).join(',');
  }

  throw new Error(`Unable to encode ${typeof target} to query string`);
}

// Take an object with unknown keys and convert it to a query string.
export function buildQueryString<T extends { [x: string]: unknown }>(variables: T) {
  return (
    '?' +
    Object.keys(variables)
      .reduce<string[]>((memo, key) => {
        memo.push(
          `${encodeURIComponent(key)}=${encodeURIComponent(
            encodeQuery(variables[key]).toString(),
          )}`,
        );
        return memo;
      }, [])
      .join('&')
  );
}

// Add a query string to an existing URL, which may or may not already have a query string.
export function appendQueryString(url: string, queryString: string): string {
  if (url.includes('?')) {
    let modifiedQueryString = `&${queryString.substring(1)}`;
    return `${url}${modifiedQueryString}`;
  }

  return `${url}${queryString}`;
}
